import * as React from "react";
import { cn } from "@/lib/utils";

export interface TileOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface ExpandableTilesProps {
  label: string;
  options: TileOption[];
  values: string[];
  onChange(values: string[]): void;
}

const TILE_ACTIVE = "bg-slate-100 border-slate-400 text-slate-900";
const TILE_IDLE = "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50";

/**
 * A row of icon tiles that widen to show their label on hover, focus, or once picked (the Reviews
 * source filter's behavior), multi-select.
 */
export const ExpandableTiles: React.FC<ExpandableTilesProps> = ({ label, options, values, onChange }) => {
  const [hovered, setHovered] = React.useState<string | null>(null);
  const [focused, setFocused] = React.useState<string | null>(null);
  const active = hovered ?? focused;

  return (
    <div className="flex h-14 w-full gap-1.5" role="group" aria-label={label} onMouseLeave={() => setHovered(null)}>
      {options.map(option => {
        const selected = values.includes(option.value);
        const showLabel = active === option.value || (selected && active == null);
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            aria-label={option.label}
            title={option.label}
            className={cn(
              "flex h-full min-w-0 basis-0 flex-col items-center justify-center overflow-hidden rounded-lg border px-1.5 transition-[flex-grow,colors] duration-300 ease-out motion-reduce:transition-none cursor-pointer",
              showLabel ? "flex-[3]" : "flex-1",
              selected ? TILE_ACTIVE : TILE_IDLE
            )}
            onMouseEnter={() => setHovered(option.value)}
            onFocus={() => setFocused(option.value)}
            onBlur={() => setFocused(current => (current === option.value ? null : current))}
            onClick={() => onChange(selected ? values.filter(v => v !== option.value) : [...values, option.value])}
          >
            <span className="flex size-5 items-center justify-center">{option.icon}</span>
            <span
              className={cn(
                "grid w-full transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
                showLabel ? "mt-1 grid-rows-[1fr] opacity-100" : "mt-0 grid-rows-[0fr] opacity-0"
              )}
              aria-hidden="true"
            >
              <span className="min-h-0 overflow-hidden">
                <span className="block truncate text-center text-[10px] font-bold uppercase leading-none tracking-wide">
                  {option.label}
                </span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};
